import { useLocalStorage } from '@/hooks/browser/use-local-storage';
import { AppBundle, AppBundleNames } from '@/utils/app-bundles';
import { getAppExternalOverrides, setAppExternalOverride } from '@/utils/dev-helper';
import { useCopilotInstruction, useCopilotContext } from '@customerio/hydra-copilot/react';
import {
  Box,
  Button,
  Checkbox,
  DataTable,
  Divider,
  Grid,
  PlainButton,
  Scrollable,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Tabs,
  TabsList,
  TabsPanel,
  TextArea,
  Truncated,
  LabelIcon,
  useSnackbars,
  useConfirmationModal,
  Code,
  Paragraph,
  Strong,
  List,
  ListItem,
  Stack,
} from '@customerio/pluma-components/react';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { createColumnHelper } from '@tanstack/table-core';
import clsx from 'clsx';
import { useEffect, useRef, useState, useMemo } from 'react';
import * as styles from './index.css';
import { servicesQuery } from '@/services/api';
import { contextualParams } from '@/services/api/ts-rest';
import { FEATURE_FLAG_DEFAULTS } from '@/services/auth/feature-flags';

export function DevHelper() {
  const isLocalEnv: boolean =
    (import.meta.env.DEV as boolean) || import.meta.env.VITE_ENABLE_EXTERNAL_UI_OVERRIDES === 'true';
  const isStandaloneEnv = __CIO_ENV__ === 'standalone';
  const isManuallyHidden = window.sessionStorage.getItem('hideDevUI') === 'true';
  const shouldShowDevHelper = (isLocalEnv || isStandaloneEnv) && !isManuallyHidden;
  const [isOpen, setIsOpen] = useLocalStorage<boolean>('dev-helper-open', false);
  const [appExternalUiEnabled, setAppExternalUiEnabled] = useState(getAppExternalOverrides());
  const [height, setHeight] = useLocalStorage<number>('dev-helper-height', 400);
  const [activeTab, setActiveTab] = useLocalStorage<
    'app_import_overrides' | 'react_query_devtools' | 'ai_prompting' | 'feature_flags'
  >('dev-helper-active-tab', isLocalEnv ? 'app_import_overrides' : 'ai_prompting');
  const isDragging = useRef(false);

  const handleMouseMove = useRef((e: MouseEvent) => {
    if (isDragging.current === false) {
      return;
    }

    const newHeight = window.innerHeight - e.clientY;
    if (newHeight < 200 || newHeight > window.innerHeight * 0.8) {
      return;
    }
    setHeight(newHeight);
    e.preventDefault();
    e.stopPropagation();
  });

  const handleMouseUp = useRef(() => {
    isDragging.current = false;
  });

  // Add and remove event listeners
  useEffect(() => {
    const mouseMoveHandler = handleMouseMove.current;
    const mouseUpHandler = handleMouseUp.current;

    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('mouseup', mouseUpHandler);

    return () => {
      window.removeEventListener('mousemove', mouseMoveHandler);
      window.removeEventListener('mouseup', mouseUpHandler);
    };
  }, []);

  if (shouldShowDevHelper === false) {
    return null;
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;

    setHeight(window.innerHeight - e.clientY);
  };

  return (
    <div className={clsx(styles.devHelper, isOpen === true && styles.devHelperOpen)} style={{ height: `${height}px` }}>
      {isOpen === true ? (
        <PlainButton
          className={styles.resizeHandle}
          onMouseDown={handleMouseDown}
          aria-label="Resize developer tools panel"
        />
      ) : null}
      <Button
        variant="tertiary"
        style={{ top: isOpen === true ? '-36px' : '-34px' }}
        icon={isOpen === true ? 'chevron-down' : 'chevron-up'}
        className={styles.toggleButton}
        aria-label="Developer tools"
        onClick={() => {
          setIsOpen(isOpen === false);
        }}
      >
        Developer tools
      </Button>
      <Tabs
        pt="075"
        display="flex"
        flexDirection="column"
        h="full"
        overflow="hidden"
        activeTab={activeTab}
        onChange={(tab) => {
          setActiveTab(tab as typeof activeTab);
        }}
        isBorderless={true}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap="100" flexShrink="0">
          <TabsList
            tabs={[
              ...(isLocalEnv
                ? [
                    { value: 'app_import_overrides', text: 'App import overrides' },
                    { value: 'feature_flags', text: 'Feature Flags' },
                    { value: 'react_query_devtools', text: 'React Query Devtools' },
                  ]
                : []),

              { value: 'ai_prompting', text: 'AI Prompting' },
            ]}
            flexShrink="0"
          />
          <Button
            variant="subtle"
            size="sm"
            onClick={() => {
              window.sessionStorage.setItem('hideDevUI', 'true');
              window.location.reload();
            }}
          >
            Hide Dev/Debug UI for this Session
          </Button>
        </Box>
        <Divider />
        {isLocalEnv ? (
          <>
            <TabsPanel value="app_import_overrides" flexGrow="1" h="full" overflowY="auto">
              <Table caption="App import overrides">
                <TableThead>
                  <TableTr>
                    <TableTh>App</TableTh>
                    <TableTh>Use external UI</TableTh>
                  </TableTr>
                </TableThead>
                <TableTbody>
                  {Object.values(AppBundle).map((app) => {
                    const externalUiEnabled = appExternalUiEnabled[app];
                    return (
                      <TableTr key={app}>
                        <TableTd>{AppBundleNames[app]}</TableTd>
                        <TableTd>
                          <Checkbox
                            id={`external_ui_enabled_${app}`}
                            isDisabled={isOpen === false}
                            isChecked={externalUiEnabled}
                            onCheckedChange={(checked) => {
                              setAppExternalOverride(app, checked);
                              setAppExternalUiEnabled(getAppExternalOverrides());
                              window.location.reload();
                            }}
                            ariaLabel={`Use external UI for ${AppBundleNames[app]}`}
                          />
                        </TableTd>
                      </TableTr>
                    );
                  })}
                </TableTbody>
              </Table>
            </TabsPanel>
            <TabsPanel value="feature_flags" flexGrow="1" h="full" overflowY="auto" p="100">
              <FeatureFlagsDataTable onToggle={() => setIsOpen(false)} />
            </TabsPanel>
            <TabsPanel value="react_query_devtools" flexGrow="1" h="full" overflowY="auto">
              <ReactQueryDevtoolsPanel />
            </TabsPanel>
          </>
        ) : null}

        <TabsPanel value="ai_prompting" flexGrow="1" h="full" overflowY="auto">
          <AIPromptingPanel />
        </TabsPanel>
      </Tabs>
    </div>
  );
}

function AIPromptingPanel() {
  const [instruction, setInstruction] = useLocalStorage<string>('dev-helper-ai-instructions', '');
  const [context, setContext] = useLocalStorage<
    {
      description: string;
      value: string;
    }[]
  >('dev-helper-ai-context', []);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editValue, setEditValue] = useState('');

  useCopilotInstruction({ instruction });

  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const addCopilotContext = () => {
    if (description.length > 0 && value.length > 0) {
      setContext([...context, { description, value }]);
      setDescription('');
      setValue('');
    }
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditDescription(context[index].description);
    setEditValue(context[index].value);
  };

  const saveEdit = () => {
    if (editingIndex === null) return;

    if (editDescription.length > 0 && editValue.length > 0) {
      const newContext = [...context];
      newContext[editingIndex] = { description: editDescription, value: editValue };
      setContext(newContext);
      setEditingIndex(null);
    }
  };

  const cancelEdit = () => {
    setEditingIndex(null);
  };

  return (
    <Box p="100">
      <Grid columns={2} gap="200" alignItems="flex-start">
        <TextArea label="Instructions" value={instruction} onInput={(e) => setInstruction(e.target.value)} rows={10} />
        <Scrollable overflow="y">
          <Table caption="Context" layout="fixed">
            <TableThead>
              <TableTr>
                <TableTh>Description</TableTh>
                <TableTh>Value</TableTh>
                <TableTh style={{ width: 150 }}>Actions</TableTh>
              </TableTr>
            </TableThead>
            <TableTbody>
              {context.map((ctx, index) => (
                <TableTr key={index}>
                  <CopilotContext description={ctx.description} value={ctx.value} />
                  {editingIndex === index ? (
                    <>
                      <TableTd>
                        <TextArea
                          label="Edit Description"
                          value={editDescription}
                          onInput={(e) => setEditDescription(e.target.value)}
                          rows={2}
                        />
                      </TableTd>
                      <TableTd>
                        <TextArea
                          label="Edit Value"
                          value={editValue}
                          onInput={(e) => setEditValue(e.target.value)}
                          rows={2}
                        />
                      </TableTd>
                      <TableTd textAlign="right">
                        <Box display="flex" gap="050" justifyContent="flex-end">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={saveEdit}
                            isDisabled={editDescription.length === 0 || editValue.length === 0}
                          >
                            Save
                          </Button>
                          <Button variant="subtle" size="sm" onClick={cancelEdit}>
                            Cancel
                          </Button>
                        </Box>
                      </TableTd>
                    </>
                  ) : (
                    <>
                      <TableTd>
                        <Truncated>{ctx.description}</Truncated>
                      </TableTd>
                      <TableTd>
                        <Truncated>{ctx.value}</Truncated>
                      </TableTd>
                      <TableTd textAlign="right">
                        <Box display="flex" gap="050" justifyContent="flex-end">
                          <Button
                            isIconOnly
                            variant="subtle"
                            size="sm"
                            onClick={() => startEditing(index)}
                            aria-label={`Edit context: ${ctx.description}`}
                            icon="edit"
                          />
                          <Button
                            isIconOnly
                            variant="subtle"
                            size="sm"
                            onClick={() => {
                              const newContext = [...context];
                              newContext.splice(index, 1);
                              setContext(newContext);
                            }}
                            aria-label={`Remove context: ${ctx.description}`}
                            icon="delete-trash"
                          />
                        </Box>
                      </TableTd>
                    </>
                  )}
                </TableTr>
              ))}
              <TableTr>
                <TableTd>
                  <TextArea
                    label="New Context Description"
                    placeholder="Enter context description"
                    value={description}
                    onInput={(e) => setDescription(e.target.value)}
                    rows={1}
                  />
                </TableTd>
                <TableTd>
                  <TextArea
                    label="New Context Value"
                    placeholder="Enter context value"
                    value={value}
                    onInput={(e) => setValue(e.target.value)}
                    rows={1}
                  />
                </TableTd>
                <TableTd textAlign="right">
                  <Button
                    variant="primary"
                    onClick={addCopilotContext}
                    disabled={description.length === 0 || value.length === 0}
                  >
                    Add Context
                  </Button>
                </TableTd>
              </TableTr>
            </TableTbody>
          </Table>
        </Scrollable>
      </Grid>
    </Box>
  );
}

function CopilotContext({ description, value }: { description: string; value: string }) {
  useCopilotContext({
    description,
    value,
  });
  return null;
}

interface FeatureFlagRow {
  id: string;
  name: string;
  enabled: boolean;
}

const featureFlagColumnHelper = createColumnHelper<FeatureFlagRow>();

const featureFlagColumns = [
  featureFlagColumnHelper.accessor('name', {
    header: 'Flag Name',
    cell: (info) => <Code>{info.getValue()}</Code>,
  }),
  featureFlagColumnHelper.accessor('enabled', {
    header: 'Enabled',
    meta: {
      align: 'center',
    },
    cell: (info) => {
      return (
        <LabelIcon
          color={info.getValue() === true ? 'green' : 'red'}
          name={info.getValue() === true ? 'check-circle' : 'close'}
        />
      );
    },
  }),
];

const featureFlagFilters = [
  {
    key: 'enabled',
    label: 'Feature Flag Value',
    type: 'radio' as const,
    options: [
      { label: 'Enabled', value: 'enabled' },
      { label: 'Disabled', value: 'disabled' },
    ],
  },
];

type FilterValue = { key: string; value: string | string[] };

function filterFeatureFlags(data: FeatureFlagRow[], filterValues: FilterValue[]) {
  let filteredData = data.slice();

  for (const filter of filterValues) {
    const { key, value } = filter;

    if (key === 'enabled') {
      filteredData = filteredData.filter((row) => {
        if (value === 'enabled') return row.enabled === true;
        if (value === 'disabled') return row.enabled === false;
        return true;
      });
    }
  }

  return filteredData;
}

function FeatureFlagsDataTable({ onToggle }: { onToggle?: () => void }) {
  const snackbars = useSnackbars();
  const confirm = useConfirmationModal();
  const [filterValues, setFilterValues] = useState<FilterValue[]>([]);

  const params = contextualParams();

  const { data, isLoading } = servicesQuery.getFeatureFlags.useQuery({
    queryKey: ['feature-flags'],
    queryData: {
      params: { accountId: params.accountId! },
    },
    enabled: params.accountId != null,
    select: (data) => ({ ...FEATURE_FLAG_DEFAULTS, ...(data.body?.featureFlags ?? {}) } as Record<string, boolean>),
  });

  const featureFlags = useMemo(() => data ?? {}, [data]);

  const updateFeatureFlagsMutation = servicesQuery.updateFeatureFlags.useMutation({
    onSuccess: () => {
      window.location.reload();
    },
    onError: (error) => {
      snackbars.create({
        message: 'Failed to update feature flags: ' + error,
        variant: 'error',
      });
    },
  });

  const formattedFeatureFlags = useMemo(() => {
    return Object.entries(featureFlags)
      .map(([key, value]) => ({
        id: key,
        name: key,
        enabled: value,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [featureFlags]);

  const filteredFeatureFlags = useMemo(() => {
    return filterFeatureFlags(formattedFeatureFlags, filterValues);
  }, [formattedFeatureFlags, filterValues]);

  const handleFilterChange = (key: string, value: string | string[]) => {
    const exists = filterValues.some((v) => v.key === key) === true;
    const updated =
      exists === true
        ? filterValues.map((v) => (v.key === key ? { ...v, value } : v))
        : [...filterValues, { key, value }];
    setFilterValues(updated);
  };

  const handleFilterClear = (key: string) => {
    setFilterValues(filterValues.filter((v) => v.key !== key));
  };

  const handleFilterClearAll = () => {
    setFilterValues([]);
  };

  const bulkActions = {
    promotedActions: [
      {
        label: 'Toggle Feature Flags',
        onClick: (selection: Record<string, boolean>) => {
          const count = Object.keys(selection).length;
          // Hopefully you aren't toggling all the flags at once
          const MAX_SELECTION_COUNT = 3;

          const selectedFlagNames = Object.entries(selection)
            .filter(([, isSelected]) => isSelected)
            .map(([flagName]) => flagName);

          const toggledFlags = Object.fromEntries(
            selectedFlagNames.map((flagName) => [flagName, featureFlags[flagName] === false]),
          );

          const updatedFlags = { ...featureFlags, ...toggledFlags };

          if (count > MAX_SELECTION_COUNT) {
            const flagsToEnable = selectedFlagNames.filter((flagName) => featureFlags[flagName] === false);
            const flagsToDisable = selectedFlagNames.filter((flagName) => featureFlags[flagName] === true);

            confirm({
              title: 'Toggle Feature Flags',
              message: (
                <Stack gap="100">
                  <Paragraph>
                    You are toggling <Strong>{count}</Strong> feature flags. Are you sure you want to continue?
                  </Paragraph>
                  {flagsToEnable.length > 0 ? (
                    <Stack gap="100">
                      <Paragraph>
                        <Strong>Enabling ({flagsToEnable.length}):</Strong>
                      </Paragraph>
                      <Scrollable withEdgeIndicators overflow="y" style={{ maxHeight: 120 }}>
                        <List gap="100" styleType="none">
                          {flagsToEnable.map((flagName) => (
                            <ListItem key={flagName}>
                              <Code>{flagName}</Code>
                            </ListItem>
                          ))}
                        </List>
                      </Scrollable>
                    </Stack>
                  ) : null}
                  {flagsToDisable.length > 0 ? (
                    <Stack gap="100">
                      <Paragraph>
                        <Strong>Disabling ({flagsToDisable.length}):</Strong>
                      </Paragraph>
                      <Scrollable withEdgeIndicators overflow="y" style={{ maxHeight: 120 }}>
                        <List gap="100" styleType="none">
                          {flagsToDisable.map((flagName) => (
                            <ListItem key={flagName}>
                              <Code>{flagName}</Code>
                            </ListItem>
                          ))}
                        </List>
                      </Scrollable>
                    </Stack>
                  ) : null}
                </Stack>
              ),
              onConfirm: () => {
                updateFeatureFlagsMutation.mutate({
                  params: contextualParams(),
                  body: updatedFlags,
                });
                onToggle?.();
              },
            });
          } else {
            updateFeatureFlagsMutation.mutate({
              params: contextualParams(),
              body: updatedFlags,
            });
            onToggle?.();
          }
        },
      },
    ],
  };

  return (
    <DataTable
      isLoading={isLoading}
      caption="Feature flags"
      data={filteredFeatureFlags}
      columns={featureFlagColumns}
      getRowId={(row) => row.id}
      withRowSelection={true}
      withSearch={true}
      search={{
        shouldSearchOnInput: true,
        shouldSearchOnClear: true,
        placeholder: 'Search flags...',
      }}
      withSorting={true}
      bulkActions={bulkActions}
      withFilters={true}
      filters={{
        filters: featureFlagFilters,
        values: filterValues,
        onChange: handleFilterChange,
        onClear: handleFilterClear,
        onClearAll: handleFilterClearAll,
      }}
    />
  );
}
